import { httpRequest, parseQuery, buildQuery, extensionSafeRedirect } from '@calendly/http-tools'

import { navigateToConfirmationPage } from 'actions/confirmation'
import {
  analyticsDataSelector,
  schedulingEventSelector,
  inviteeDataSelector,
} from 'selectors/scheduling'
import { maybeStoreData } from 'services/autofill'
import {
  setGeneralError,
  setScheduleError
} from 'actions/errors'
import { eventTypeSelector, setInvitee } from 'root-api'
import { trackingSelector } from 'selectors/tracking'
import { currentInviteeSelector } from 'selectors/current_invitee'
import { isPublisherSelector } from 'selectors'

import { sanitizeEncoding } from '../services/url_helpers'

const getReschedulingData = async (state, { spotStartTime, formData }) => {
  const { uuid: eventTypeUuid, durationOverride } = eventTypeSelector(state)

  const eventFields = formData.event_fields.map(
    ({
      id: _id,
      extension: _extension,
      isValid: _isValid,
      countryCode: _countryCode,
      ...rest
    }) => rest
  )

  return {
    analytics: analyticsDataSelector(state),
    event: schedulingEventSelector(state, { spotStartTime, durationOverride, formData }),
    event_fields: eventFields,
    event_type_uuid: eventTypeUuid,
    invitee: inviteeDataSelector(state, { formData }),
    rescheduling: {
      is_publisher: isPublisherSelector(state),
      invitee_uuid: currentInviteeSelector(state).uuid,
      new_event_user_uuid: parseQuery(window.location.search).new_event_user_uuid
    },
    tracking: trackingSelector(state)
  }
}

const handleErrorResponse = (responseData) => (
  responseData.payload && responseData.payload.failure
    ? setGeneralError(responseData)
    : setScheduleError(responseData)
)

const handleSuccessResponse = (responseData) => ({
  type: 'SCHEDULING_DONE',
  payload: responseData
})

const rescheduleRedirectPathSelector = (state, responseData) => {
  const { return_to: url, client, platform } = parseQuery(window.location.search)
  const { kind } = eventTypeSelector(state)

  const query = { rescheduled: kind === 'group' ? 'invitee' : 'event' }
  if (responseData?.event && client) {
    query.eventUuid = responseData.event.uuid
  }
  const queryStr = buildQuery(query)

  if (url) {
    const sanitizedUrl = platform === 'outlook' ? url : sanitizeEncoding(url)
    return `${sanitizedUrl}?${queryStr}`
  }
  return `/app/scheduled_events/user/me?${queryStr}`
}

const afterRescheduleNavigation = (formData, responseData) => (dispatch, getState, { history }) => {
  const state = getState()

  if (isPublisherSelector(state)) {
    const { platform } = parseQuery(window.location.search)
    const redirectUrl = rescheduleRedirectPathSelector(state, responseData)
    if (platform === 'outlook') {
      extensionSafeRedirect(redirectUrl, true, { success: true, id: responseData.uuid })
    } else {
      window.top.location.href = redirectUrl
    }
  } else {
    // For non-publishers, navigate directly to confirmation using response UUID
    // This avoids selector timing issues that cause flash
    const eventType = eventTypeSelector(state)
    if (eventType?.confirmation_page_type === 'external') {
      dispatch(navigateToConfirmationPage(formData))
    } else if (responseData?.uuid) {
      history.push(`/reschedulings/${responseData.uuid}/confirmation`)
    } else {
      dispatch(navigateToConfirmationPage(formData))
    }
  }
}

export const reschedule = (spotStartTime, formData, uuid, requestId) => async (dispatch, getState) => {
  dispatch({ type: 'SCHEDULING_REQUEST' })

  const state = getState()
  const data = await getReschedulingData(state, { spotStartTime, formData })

  const headers = {
    'Content-Type': 'application/json',
    'Booking-Request-ID': requestId
  }

  const [success, responseData] = await httpRequest({
    url: `/api/booking/invitees/${uuid}`,
    method: 'PATCH',
    headers,
    data
  })

  if (success) {
    dispatch(handleSuccessResponse(responseData))
    maybeStoreData(state, eventTypeSelector(state), formData.invitee)

    // Update RTK Query cache before navigation for non-publishers
    // This ensures useInvitee() can read from cache (which it prefers) when confirmation page loads
    // Safe to do before navigation since we build path directly (not using selector chain)
    if (!isPublisherSelector(state)) {
      dispatch(setInvitee(responseData))
    }

    dispatch(afterRescheduleNavigation(formData, responseData))
  } else {
    dispatch(handleErrorResponse(responseData))
  }
}
